import React, { type SyntheticEvent } from 'react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import Button, { IconButton } from '@atlaskit/button/new';
import EyeOpen from '@atlaskit/icon/core/eye-open';
import EyeOpenFilledIcon from '@atlaskit/icon/core/eye-open-filled';
import { Text } from '@atlaskit/primitives';
import { B400 } from '@atlaskit/theme/colors';
import { token } from '@atlaskit/tokens';
import AkTooltip from '@atlaskit/tooltip';
import { useIntl } from '@atlassian/jira-intl';
import TransitiveNumber from '@atlassian/react-transitive-number';
import messages from '../action-button/messages.tsx';

export type WatchersButtonProps = {
	isWatching: boolean;
	isKeyboardShortcutEnabled: boolean;
	canViewDropdown: boolean;
	isDropdownOpen: boolean;
	onClick?: (event: SyntheticEvent) => void;
	onOpenDropdownAnalytics: (arg1: number, arg2: UIAnalyticsEvent) => void;
	watchersCount: number;
	onToggleWatching: (arg1: SyntheticEvent, arg2: UIAnalyticsEvent) => void;
};

type GetTooltipMessageProps = {
	isKeyboardShortcutEnabled: boolean;
	isWatching: boolean;
	canViewDropdown: boolean;
};

const getCounterDisplay = (watchersCount: number) => {
	const maxValue = 99;

	if (watchersCount <= 0) {
		return '';
	}

	if (watchersCount > maxValue) {
		return `${maxValue}+`;
	}

	return watchersCount;
};

const getTooltipMessage = ({
	canViewDropdown,
	isKeyboardShortcutEnabled,
	isWatching,
}: GetTooltipMessageProps) => {
	if (canViewDropdown) {
		return messages.watchersOptionsTooltip;
	}

	if (isKeyboardShortcutEnabled) {
		return isWatching ? messages.unwatchTooltipWithHotkey : messages.watchTooltipWithHotkey;
	}
	return isWatching ? messages.unwatchTooltip : messages.watchTooltip;
};

const watchedIcon = (
	<EyeOpenFilledIcon
		testId="issue-view-watchers.watchers.watchers-button-refresh.watch-icon-filled"
		label=""
		color={token('color.icon.selected', B400)}
		spacing="spacious"
	/>
);

const unwatchedIcon = (
	<EyeOpen
		testId="issue-view-watchers.watchers.watchers-button-refresh.watch-icon"
		label=""
		color="currentColor"
		spacing="spacious"
	/>
);

const WatchersButtonRefresh = ({
	isWatching,
	isDropdownOpen,
	isKeyboardShortcutEnabled,
	canViewDropdown,
	watchersCount,
	onToggleWatching,
	onClick,
	onOpenDropdownAnalytics,
}: WatchersButtonProps) => {
	const { formatMessage } = useIntl();

	const getOnClick = () => {
		if (canViewDropdown) {
			return (clickEvent: SyntheticEvent, analyticsEvent: UIAnalyticsEvent) => {
				if (onClick && typeof onClick === 'function') {
					onClick(clickEvent);
				}
				return !isDropdownOpen && onOpenDropdownAnalytics(watchersCount, analyticsEvent);
			};
		}

		return onToggleWatching;
	};

	const icon = isWatching ? watchedIcon : unwatchedIcon;

	const buttonAriaLabel = isWatching
		? formatMessage(messages.watchingLabel, { watchersCount })
		: formatMessage(messages.notWatchingLabel, { watchersCount });

	return (
		<AkTooltip
			/* eslint-disable-next-line jira/integration/test-id-by-folder-structure */
			testId="issue.watchers.action-button.tooltip"
			content={formatMessage(
				getTooltipMessage({ canViewDropdown, isKeyboardShortcutEnabled, isWatching }),
			)}
			position="top"
		>
			{watchersCount !== 0 ? (
				<Button
					appearance="default"
					spacing="default"
					onClick={getOnClick()}
					isSelected={isWatching}
					iconBefore={() => icon}
					aria-label={buttonAriaLabel}
					aria-expanded={isDropdownOpen}
					// eslint-disable-next-line jira/integration/test-id-by-folder-structure
					testId="issue.watchers.action-button.root"
					interactionName="auto-generated.jira-issue-view-watchers.watchers.view.action-button.button"
				>
					<Text
						{...(isWatching ? { color: 'color.text.selected' } : {})}
						// eslint-disable-next-line jira/integration/test-id-by-folder-structure
						testId="issue.watchers.action-button.counter"
					>
						<TransitiveNumber>{getCounterDisplay(watchersCount)}</TransitiveNumber>
					</Text>
				</Button>
			) : (
				<IconButton
					appearance="default"
					spacing="default"
					onClick={getOnClick()}
					isSelected={isDropdownOpen}
					icon={() => icon}
					label={buttonAriaLabel}
					aria-expanded={isDropdownOpen}
					// eslint-disable-next-line jira/integration/test-id-by-folder-structure
					testId="issue.watchers.action-button.root"
					interactionName="auto-generated.jira-issue-view-watchers.watchers.view.action-button.button"
				/>
			)}
		</AkTooltip>
	);
};

export default WatchersButtonRefresh;
